import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, Check, ChevronRight, ChevronLeft, FileText, Image as ImageIcon, Crown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = ["Identity", "Eligibility", "Delegation", "Biography", "Photos", "Documents", "Agreements", "Review"];

export default function Apply() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [delegations, setDelegations] = useState([]);
  const [app, setApp] = useState({ identity: {}, eligibility: {}, delegation_code: "", biography: {}, photos: [], documents: [], agreements: {} });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    api.get("/delegations").then((r) => setDelegations(r.data)).catch(() => {});
    if (user) api.get("/me/application").then((r) => { if (r.data && r.data.status) setApp((a) => ({ ...a, ...r.data })); }).catch(() => {});
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const submitted = ["submitted", "under_review", "interview", "approved", "rejected", "waitlisted"].includes(app.status);

  const save = async (submit = false) => {
    setSaving(true);
    try {
      const { data } = await api.post("/me/application", { ...app, step, submit });
      setApp((a) => ({ ...a, ...data }));
      toast.success(submit ? "Application submitted!" : "Progress saved");
      return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setSaving(false); }
  };

  const upload = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setApp((a) => ({ ...a, [kind === "photo" ? "photos" : "documents"]: [...(kind === "photo" ? a.photos : a.documents), data] }));
      toast.success(`${data.filename} uploaded`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(""); }
  };

  const setField = (section, key, val) => setApp((a) => ({ ...a, [section]: { ...a[section], [key]: val } }));
  const Input = ({ section, name, label, type = "text" }) => (
    <label className="block"><span className="mb-1 block text-xs uppercase tracking-wider text-amber-100/60">{label}</span>
      <input type={type} value={app[section]?.[name] || ""} onChange={(e) => setField(section, name, e.target.value)} data-testid={`apply-${section}-${name}`}
        className="w-full rounded-lg border border-gold/25 bg-obsidian px-4 py-2.5 text-sm text-amber-50 outline-none focus:border-gold" /></label>
  );

  if (submitted && step < STEPS.length - 1) {
    return (
      <div className="section py-16" data-testid="apply-page">
        <div className="glass mx-auto max-w-lg rounded-2xl p-10 text-center">
          <Crown className="mx-auto mb-4 h-9 w-9 text-gold" />
          <h1 className="text-3xl text-amber-50">Application {app.status?.replace("_", " ")}</h1>
          <p className="mt-3 text-amber-100/60">Thank you, {user.name}. Track your progress from your dashboard.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button onClick={() => setStep(STEPS.length - 1)} className="btn-ghost-gold">Review submission</button>
            <button onClick={() => navigate("/account")} className="btn-gold">My Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section py-10" data-testid="apply-page">
      <p className="kicker mb-2">Become a Delegate</p>
      <h1 className="text-4xl sm:text-5xl text-amber-50">Candidate Application</h1>

      {/* Progress */}
      <div className="mt-8 flex flex-wrap gap-1.5" data-testid="apply-steps">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-all ${i === step ? "gold-bg text-obsidian" : i < step ? "border border-emerald-accent/50 text-emerald-accent" : "border border-gold/20 text-amber-50/50"}`}>
            {i < step && <Check className="h-3 w-3" />} {s}
          </button>
        ))}
      </div>

      <div className="mt-8 card-lux p-6 sm:p-8">
        {step === 0 && <div className="grid gap-4 sm:grid-cols-2"><Input section="identity" name="full_name" label="Full legal name" /><Input section="identity" name="phone" label="Phone" /><Input section="identity" name="dob" label="Date of birth" type="date" /><Input section="identity" name="nationality" label="Nationality" /></div>}
        {step === 1 && <div className="space-y-4">
          <p className="text-sm text-amber-100/60">Confirm your eligibility (demo — no legal claims).</p>
          {[["age_ok", "I am between 18 and 28 years old"], ["citizen", "I am a citizen or resident of my chosen delegation"], ["unmarried", "I meet the delegation's eligibility requirements"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-3 text-sm text-amber-50"><input type="checkbox" checked={!!app.eligibility?.[k]} onChange={(e) => setField("eligibility", k, e.target.checked)} className="accent-[#E6005C] h-4 w-4" data-testid={`apply-elig-${k}`} /> {l}</label>
          ))}
        </div>}
        {step === 2 && <div><span className="mb-1 block text-xs uppercase tracking-wider text-amber-100/60">Select your delegation</span>
          <select value={app.delegation_code} onChange={(e) => setApp((a) => ({ ...a, delegation_code: e.target.value }))} data-testid="apply-delegation"
            className="w-full rounded-lg border border-gold/25 bg-obsidian px-4 py-3 text-sm text-amber-50 outline-none focus:border-gold">
            <option value="">Choose…</option>{delegations.map((d) => <option key={d.code} value={d.code}>{d.flag} {d.name}</option>)}
          </select></div>}
        {step === 3 && <div className="space-y-4"><Input section="biography" name="profession" label="Profession" /><Input section="biography" name="advocacy" label="Advocacy platform" />
          <label className="block"><span className="mb-1 block text-xs uppercase tracking-wider text-amber-100/60">Your story</span>
            <textarea rows={5} value={app.biography?.story || ""} onChange={(e) => setField("biography", "story", e.target.value)} data-testid="apply-bio-story" className="w-full rounded-lg border border-gold/25 bg-obsidian px-4 py-2.5 text-sm text-amber-50 outline-none focus:border-gold" /></label></div>}
        {step === 4 && <Uploader kind="photo" Icon={ImageIcon} accept="image/*" label="Portrait & gallery photos" items={app.photos} uploading={uploading} onUpload={upload} testid="apply-photo-upload" />}
        {step === 5 && <Uploader kind="document" Icon={FileText} accept=".pdf,image/*" label="ID & consent documents (PDF/image)" items={app.documents} uploading={uploading} onUpload={upload} testid="apply-doc-upload" />}
        {step === 6 && <div className="space-y-4">{[["rules", "I agree to the competition rules and code of conduct"], ["media", "I consent to the use of my submitted media for the competition"], ["truth", "I confirm all information provided is accurate"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-3 text-sm text-amber-50"><input type="checkbox" checked={!!app.agreements?.[k]} onChange={(e) => setField("agreements", k, e.target.checked)} className="accent-[#E6005C] h-4 w-4" data-testid={`apply-agree-${k}`} /> {l}</label>))}</div>}
        {step === 7 && <div className="space-y-3 text-sm">
          <h3 className="font-serif text-xl text-amber-50">Review & Submit</h3>
          <Row label="Name" value={app.identity?.full_name} />
          <Row label="Delegation" value={delegations.find((d) => d.code === app.delegation_code)?.name} />
          <Row label="Advocacy" value={app.biography?.advocacy} />
          <Row label="Photos" value={`${app.photos?.length || 0} uploaded`} />
          <Row label="Documents" value={`${app.documents?.length || 0} uploaded`} />
          <Row label="Status" value={app.status || "draft"} />
        </div>}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button onClick={() => setStep((s) => Math.max(s - 1, 0))} disabled={step === 0} className="inline-flex items-center gap-1 text-sm text-gold disabled:opacity-30"><ChevronLeft className="h-4 w-4" /> Back</button>
        <div className="flex gap-2">
          <button onClick={() => save(false)} disabled={saving} className="btn-ghost-gold inline-flex items-center gap-2" data-testid="apply-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save draft</button>
          {step < STEPS.length - 1
            ? <button onClick={async () => { if (await save(false)) setStep((s) => s + 1); }} className="btn-gold inline-flex items-center gap-2" data-testid="apply-next">Next <ChevronRight className="h-4 w-4" /></button>
            : <button onClick={() => save(true)} disabled={saving} className="btn-gold inline-flex items-center gap-2" data-testid="apply-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Submit Application</button>}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (<div className="flex justify-between border-b border-gold/10 py-2"><span className="text-muted-foreground">{label}</span><span className="text-amber-50">{value || "—"}</span></div>);

function Uploader({ kind, Icon, accept, label, items, uploading, onUpload, testid }) {
  return (
    <div>
      <span className="mb-3 block text-xs uppercase tracking-wider text-amber-100/60">{label}</span>
      <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gold/30 py-10 text-center hover:border-gold/60" data-testid={testid}>
        {uploading === kind ? <Loader2 className="h-6 w-6 animate-spin text-gold" /> : <Upload className="h-6 w-6 text-gold" />}
        <span className="text-sm text-amber-100/70">Click to upload {kind === "photo" ? "a photo" : "a document"}</span>
        <input type="file" accept={accept} className="hidden" onChange={(e) => onUpload(e.target.files[0], kind)} />
      </label>
      <div className="mt-4 space-y-2">
        {(items || []).map((f, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg border border-gold/15 bg-obsidian px-3 py-2 text-sm text-amber-50">
            <Icon className="h-4 w-4 text-gold" /> <span className="flex-1 truncate">{f.filename}</span> <Check className="h-4 w-4 text-emerald-accent" />
          </div>
        ))}
      </div>
    </div>
  );
}
